import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { createCustomTheme } from '../utils/theme';
import { getStorageItem, setStorageItem } from '../utils/helpers';
import { CALIFORNIA_OFFICES } from '../utils/constants';

// Initial state
const initialState = {
  // UI State
  darkMode: getStorageItem('darkMode', false),
  sidebarOpen: getStorageItem('sidebarOpen', true),
  loading: false,
  error: null,

  // User State
  user: getStorageItem('user', null),
  isAuthenticated: !!getStorageItem('auth_token'),

  // App Data
  offices: CALIFORNIA_OFFICES,
  selectedOffices: getStorageItem('selectedOffices', []),
  notifications: [],
  unreadCount: 0,

  // Filters and Preferences
  filters: {
    dateRange: '30d',
    office: 'all',
    status: 'all',
    type: 'all',
  },
  preferences: getStorageItem('preferences', {
    language: 'en',
    timezone: 'America/Los_Angeles',
    notifications: {
      email: true,
      push: true,
      reviews: true,
      posts: true,
      analytics: false,
    },
    dashboard: {
      layout: 'grid',
      cards: ['overview', 'recent-posts', 'recent-reviews', 'analytics'],
    },
  }),

  // Real-time data
  realtimeData: {
    posts: {},
    reviews: {},
    analytics: {},
  },
};

// Action types
const actionTypes = {
  // UI Actions
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TOGGLE_DARK_MODE: 'TOGGLE_DARK_MODE',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_SIDEBAR_OPEN: 'SET_SIDEBAR_OPEN',

  // User Actions
  SET_USER: 'SET_USER',
  SET_AUTHENTICATED: 'SET_AUTHENTICATED',
  LOGOUT: 'LOGOUT',

  // Office Actions
  SET_OFFICES: 'SET_OFFICES',
  SET_SELECTED_OFFICES: 'SET_SELECTED_OFFICES',
  UPDATE_OFFICE: 'UPDATE_OFFICE',

  // Notification Actions
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  CLEAR_NOTIFICATIONS: 'CLEAR_NOTIFICATIONS',
  SET_UNREAD_COUNT: 'SET_UNREAD_COUNT',

  // Filter Actions
  SET_FILTERS: 'SET_FILTERS',
  UPDATE_FILTER: 'UPDATE_FILTER',
  RESET_FILTERS: 'RESET_FILTERS',

  // Preference Actions
  SET_PREFERENCES: 'SET_PREFERENCES',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',

  // Real-time data actions
  UPDATE_REALTIME_DATA: 'UPDATE_REALTIME_DATA',
  SET_POSTS_DATA: 'SET_POSTS_DATA',
  SET_REVIEWS_DATA: 'SET_REVIEWS_DATA',
  SET_ANALYTICS_DATA: 'SET_ANALYTICS_DATA',
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case actionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case actionTypes.TOGGLE_DARK_MODE:
      const newDarkMode = !state.darkMode;
      setStorageItem('darkMode', newDarkMode);
      return {
        ...state,
        darkMode: newDarkMode,
      };

    case actionTypes.TOGGLE_SIDEBAR:
      const newSidebarOpen = !state.sidebarOpen;
      setStorageItem('sidebarOpen', newSidebarOpen);
      return {
        ...state,
        sidebarOpen: newSidebarOpen,
      };

    case actionTypes.SET_SIDEBAR_OPEN:
      setStorageItem('sidebarOpen', action.payload);
      return {
        ...state,
        sidebarOpen: action.payload,
      };

    case actionTypes.SET_USER:
      setStorageItem('user', action.payload);
      return {
        ...state,
        user: action.payload,
      };

    case actionTypes.SET_AUTHENTICATED:
      return {
        ...state,
        isAuthenticated: action.payload,
      };

    case actionTypes.LOGOUT:
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        isAuthenticated: false,
      };

    case actionTypes.SET_OFFICES:
      return {
        ...state,
        offices: action.payload,
      };

    case actionTypes.SET_SELECTED_OFFICES:
      setStorageItem('selectedOffices', action.payload);
      return {
        ...state,
        selectedOffices: action.payload,
      };

    case actionTypes.UPDATE_OFFICE:
      return {
        ...state,
        offices: state.offices.map(office =>
          office.id === action.payload.id
            ? { ...office, ...action.payload.data }
            : office
        ),
      };

    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1,
      };

    case actionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        ),
      };

    case actionTypes.CLEAR_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
      };

    case actionTypes.SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.payload,
      };

    case actionTypes.SET_FILTERS:
      return {
        ...state,
        filters: action.payload,
      };

    case actionTypes.UPDATE_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.payload.key]: action.payload.value,
        },
      };

    case actionTypes.RESET_FILTERS:
      return {
        ...state,
        filters: initialState.filters,
      };

    case actionTypes.SET_PREFERENCES:
      setStorageItem('preferences', action.payload);
      return {
        ...state,
        preferences: action.payload,
      };

    case actionTypes.UPDATE_PREFERENCES:
      const newPreferences = {
        ...state.preferences,
        ...action.payload,
      };
      setStorageItem('preferences', newPreferences);
      return {
        ...state,
        preferences: newPreferences,
      };

    case actionTypes.UPDATE_REALTIME_DATA:
      return {
        ...state,
        realtimeData: {
          ...state.realtimeData,
          [action.payload.type]: {
            ...state.realtimeData[action.payload.type],
            ...action.payload.data,
          },
        },
      };

    case actionTypes.SET_POSTS_DATA:
      return {
        ...state,
        realtimeData: {
          ...state.realtimeData,
          posts: action.payload,
        },
      };

    case actionTypes.SET_REVIEWS_DATA:
      return {
        ...state,
        realtimeData: {
          ...state.realtimeData,
          reviews: action.payload,
        },
      };

    case actionTypes.SET_ANALYTICS_DATA:
      return {
        ...state,
        realtimeData: {
          ...state.realtimeData,
          analytics: action.payload,
        },
      };

    default:
      return state;
  }
}

// Context
const AppContext = createContext();

// Provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Create theme based on dark mode preference
  const theme = createCustomTheme(state.darkMode);

  // Action creators
  const actions = {
    setLoading: (loading) => 
      dispatch({ type: actionTypes.SET_LOADING, payload: loading }),

    setError: (error) => 
      dispatch({ type: actionTypes.SET_ERROR, payload: error }),

    clearError: () => 
      dispatch({ type: actionTypes.CLEAR_ERROR }),

    toggleDarkMode: () => 
      dispatch({ type: actionTypes.TOGGLE_DARK_MODE }),

    toggleSidebar: () => 
      dispatch({ type: actionTypes.TOGGLE_SIDEBAR }),

    setSidebarOpen: (open) => 
      dispatch({ type: actionTypes.SET_SIDEBAR_OPEN, payload: open }),

    setUser: (user) => 
      dispatch({ type: actionTypes.SET_USER, payload: user }),

    setAuthenticated: (isAuth) => 
      dispatch({ type: actionTypes.SET_AUTHENTICATED, payload: isAuth }),

    logout: () => 
      dispatch({ type: actionTypes.LOGOUT }),

    setOffices: (offices) => 
      dispatch({ type: actionTypes.SET_OFFICES, payload: offices }),

    setSelectedOffices: (officeIds) => 
      dispatch({ type: actionTypes.SET_SELECTED_OFFICES, payload: officeIds }),

    updateOffice: (id, data) => 
      dispatch({ type: actionTypes.UPDATE_OFFICE, payload: { id, data } }),

    addNotification: (notification) => 
      dispatch({ 
        type: actionTypes.ADD_NOTIFICATION, 
        payload: { 
          id: Date.now(), 
          timestamp: new Date(), 
          ...notification 
        } 
      }),

    removeNotification: (id) => 
      dispatch({ type: actionTypes.REMOVE_NOTIFICATION, payload: id }),

    clearNotifications: () => 
      dispatch({ type: actionTypes.CLEAR_NOTIFICATIONS }),

    setUnreadCount: (count) => 
      dispatch({ type: actionTypes.SET_UNREAD_COUNT, payload: count }),

    setFilters: (filters) => 
      dispatch({ type: actionTypes.SET_FILTERS, payload: filters }),

    updateFilter: (key, value) => 
      dispatch({ type: actionTypes.UPDATE_FILTER, payload: { key, value } }),

    resetFilters: () => 
      dispatch({ type: actionTypes.RESET_FILTERS }),

    setPreferences: (preferences) => 
      dispatch({ type: actionTypes.SET_PREFERENCES, payload: preferences }),

    updatePreferences: (preferences) => 
      dispatch({ type: actionTypes.UPDATE_PREFERENCES, payload: preferences }),

    updateRealtimeData: (type, data) => 
      dispatch({ type: actionTypes.UPDATE_REALTIME_DATA, payload: { type, data } }),

    setPostsData: (data) => 
      dispatch({ type: actionTypes.SET_POSTS_DATA, payload: data }),

    setReviewsData: (data) => 
      dispatch({ type: actionTypes.SET_REVIEWS_DATA, payload: data }),

    setAnalyticsData: (data) => 
      dispatch({ type: actionTypes.SET_ANALYTICS_DATA, payload: data }),
  };

  // Computed values
  const computed = {
    activeOffices: state.offices.filter(office => office.status === 'active'),
    selectedOfficeObjects: state.offices.filter(office => 
      state.selectedOffices.includes(office.id)
    ),
    hasSelectedOffices: state.selectedOffices.length > 0,
    allOfficesSelected: state.selectedOffices.length === state.offices.length,
  };

  const value = {
    ...state,
    ...actions,
    ...computed,
    theme,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the context
export function useAppContext() {
  const context = useContext(AppContext);
  
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  
  return context;
}

export default AppContext;